import { Stack } from '@mui/material';
import { FlowNotableObject, type FlowUpdateNotableObjectConfig } from 'flow-schema';
import { type ComponentType } from 'react';

import { isDefined } from 'common';

import { UpdateAssistantFormFields } from './FormFields/UpdateAssistantFormFields';
import { UpdateEncounterFormFields } from './FormFields/encounterFormFields';
import { UpdatePatientFormFields } from './FormFields/patientFormFields';
import { UpdatePatientInsuranceInformationFormFields } from './FormFields/patientInsuranceInformationFormFields';
import { UpdatePatientOrderFormFields } from './FormFields/patientOrderFormFields';
import { UpdatePatientUploadFormFields } from './FormFields/patientUploadFormFields';
import { UpdatePaymentFormFields } from './FormFields/paymentFormFields';
import { UpdateProviderFormFields } from './FormFields/providerFormFields';
import { useApiFlowDefinition } from '../../../../Definitions/Definition/context/useApiFlowDefinition';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const PROD_KRIEGER_ORGANIZATION_ID = '45156d5d-9abe-43f4-b3e4-68cf6d11c799' as const;
const PROD_CHN_ORGANIZATION_ID = '38b6db06-0c01-40a0-b3ea-f105f5d52b9c' as const;
const PROD_CATHOLIC_HEALTH_ORGANIZATION_ID = 'a456d45e-1cc6-4ea8-a3ec-7c36a5ac3fd8' as const;
const PROD_MUSC_ORGANIZATION_ID = '3146707b-b97f-4dc8-a784-d3ee48c6149a' as const;
const PROD_LMH_ORGANIZATION_ID = '34720baa-e2ce-48d2-b55a-3a5da0cd94d5' as const;
const PROD_PHS_ORGANIZATION_ID = '36ed7bf7-fd18-4532-9669-9075f0e4bb70' as const;

// TODO: Remove Partial<> once all NotableObjects are implemented here
const UPDATE_NOTABLE_OBJECT_FORM_FIELDS: Partial<Record<FlowNotableObject, ComponentType>> = {
  [FlowNotableObject.Assistant]: UpdateAssistantFormFields,
  [FlowNotableObject.Encounter]: UpdateEncounterFormFields,
  [FlowNotableObject.PatientOrder]: UpdatePatientOrderFormFields,
  [FlowNotableObject.Payment]: UpdatePaymentFormFields,
  [FlowNotableObject.Provider]: UpdateProviderFormFields,
  [FlowNotableObject.Patient]: UpdatePatientFormFields,
  [FlowNotableObject.PatientInsuranceInformation]: UpdatePatientInsuranceInformationFormFields,
  [FlowNotableObject.PatientUpload]: UpdatePatientUploadFormFields,
};

export function UpdateNotableObjectFormFields() {
  const { value: notableObject } =
    useSelectedNodeField<FlowUpdateNotableObjectConfig['notableObject']>('config.notableObject');
  const FormFields = isDefined(notableObject)
    ? UPDATE_NOTABLE_OBJECT_FORM_FIELDS[notableObject]
    : undefined;

  const {
    flowDefinitionResponse: { data: flowDefinitionWithFlow },
  } = useApiFlowDefinition();
  const flowOrganizationId = flowDefinitionWithFlow?.organizationId;

  return (
    <Stack gap={3}>
      <BlockSelectField
        label="Notable Object"
        fieldName="config.notableObject"
        required
        options={Object.keys(UPDATE_NOTABLE_OBJECT_FORM_FIELDS)
          // Only show Assistant in the dropdown if the organization is Krieger, CHN, Catholic Health, MUSC, LMH, or PHS
          // TODO: remove this once assistant is configured within the notable platform (instead of VAPI)
          // https://app.asana.com/1/1187495389489253/project/1204809327547775/task/1210734028328533?focus=true
          .filter(
            (notableObject) =>
              !(
                (notableObject as FlowNotableObject) === FlowNotableObject.Assistant &&
                import.meta.env.PROD &&
                isDefined(flowOrganizationId) &&
                ![
                  PROD_KRIEGER_ORGANIZATION_ID,
                  PROD_CHN_ORGANIZATION_ID,
                  PROD_CATHOLIC_HEALTH_ORGANIZATION_ID,
                  PROD_MUSC_ORGANIZATION_ID,
                  PROD_LMH_ORGANIZATION_ID,
                  PROD_PHS_ORGANIZATION_ID,
                ].some((id) => id === flowOrganizationId)
              )
          )
          .map((notableObject) => ({
            label: notableObject,
            value: notableObject,
          }))}
      />
      {isDefined(FormFields) && <FormFields />}
    </Stack>
  );
}
